import { getEntryTypeIcon } from 'features/search/config'
import React from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

interface CategoryFiltersProps {
  counts: Partial<Record<RolodexEntryType, number>>
  activeFilters: Set<RolodexEntryType>
  onToggleFilter: (type: RolodexEntryType) => void
}

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #3a3a3a;
`

const FilterLabel = styled.div`
  font-size: 0.75rem;
  color: #999999;
  margin-right: 0.25rem;
  display: flex;
  align-items: center;
`

const FilterChip = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid ${(props) => (props.$isActive ? '#ff8c00' : '#3a3a3a')};
  border-radius: 1rem;
  background-color: ${(props) => (props.$isActive ? '#2a1a0a' : '#2a2a2a')};
  color: ${(props) => (props.$isActive ? '#ff8c00' : '#ffffff')};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    border-color: ${(props) => (props.$isActive ? '#ff8c00' : '#4a4a4a')};
    background-color: ${(props) => (props.$isActive ? '#3a2a0a' : '#3a3a3a')};
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const FilterCount = styled.span`
  font-weight: 600;
`

const CloseIcon = styled.span`
  font-size: 1rem;
  line-height: 1;
  margin-left: 0.125rem;
`

const formatType = (type: string): string => {
  return type.replace(/-/g, ' ')
}

export const CategoryFilters: React.FC<CategoryFiltersProps> = ({
  counts,
  activeFilters,
  onToggleFilter,
}) => {
  const sortedTypes = Object.entries(counts)
    .filter((entry) => entry[1] > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([type]) => type as RolodexEntryType)

  if (sortedTypes.length === 0) {
    return null
  }

  return (
    <FiltersContainer>
      <FilterLabel>Filter by:</FilterLabel>
      {sortedTypes.map((type) => {
        const TypeIcon = getEntryTypeIcon(type)
        const isActive = activeFilters.has(type)
        const count = counts[type] || 0
        const formattedType = formatType(type)

        return (
          <FilterChip
            key={type}
            $isActive={isActive}
            onClick={() => onToggleFilter(type)}
            aria-pressed={isActive}
            aria-label={`${isActive ? 'Remove' : 'Add'} ${formattedType} filter`}>
            <TypeIcon />
            <span style={{ textTransform: 'capitalize' }}>{formattedType}</span>
            <FilterCount>{count}</FilterCount>
            {isActive && <CloseIcon>×</CloseIcon>}
          </FilterChip>
        )
      })}
    </FiltersContainer>
  )
}
